/**
 * Message pressure, where a campaign is scheduled — slice 59.
 *
 * Sense's Pressure page says who has been messaged too often; this is where that
 * becomes a decision. Before a send, it says how many of the people this
 * campaign would try are already at Critical (5 or more messages in 30 days) and
 * how many this send would put there, and offers one switch: rest the ones at
 * Critical for this campaign. Resting writes them as `rested` recipient rows —
 * visible, counted, never silently dropped.
 *
 * ## WHEN IT DRAWS NOTHING
 *
 * - The workspace's plan does not include Sense. Pressure is a Sense signal, and
 *   a pillar a workspace has not got does not leak onto a Reach screen.
 * - Nobody is at Critical and nobody would reach it. A clear "no pressure" line
 *   would be a sentence about nothing on the one screen where every line is read
 *   before a real send.
 * - The read failed. The schedule does not depend on it — resting is optional,
 *   and off unless chosen — so a failed advisory says so in one line and the
 *   switch is withheld rather than offered over numbers nobody has.
 *
 * The switch only appears when there is somebody at Critical to rest: a switch
 * that would rest nobody is a control with no effect. And whenever it is not
 * offered — nobody at Critical, the read failed, the plan lacks Sense — it is
 * turned OFF, so a box ticked before a refetch hid it cannot go on resting
 * people from a screen that no longer shows it.
 *
 * ## THE NUMBERS ARE A READING, AND THE LABEL SAYS "EVERYONE"
 *
 * The counts are read when the panel opens; the resting happens when Schedule
 * is pressed, over whoever is at Critical then. A campaign still sending in
 * between can move that, so the switch says "everyone at Critical" and leaves
 * the number to the title above it, where it reads as what it is. Suppressed
 * people are not counted (the send marks them `suppressed` first); messages
 * already scheduled but not yet sent are not counted either — pressure counts
 * what was accepted, and a send not yet made has pressed on nobody.
 */
import { useEffect } from 'react';
import { Callout, Checkbox } from '@wend/ui';
import {
  PRESSURE_CRITICAL_AT,
  PRESSURE_WINDOW_DAYS,
  type Channel,
} from '@wend/shared';

import { useAuth } from '../../auth/auth-context';
import { fetchSegmentPressure } from '../../lib/api/sense';
import { formatCount } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';

export interface SchedulePressureNoticeProps {
  readonly segmentId: string;
  readonly channel: Channel;
  readonly restCritical: boolean;
  readonly onRestCriticalChange: (value: boolean) => void;
  readonly disabled: boolean;
}

export function SchedulePressureNotice(props: SchedulePressureNoticeProps) {
  const { user } = useAuth();
  const entitled = user !== null && user.features.includes('sense');
  const { restCritical, onRestCriticalChange } = props;
  useEffect(() => {
    if (!entitled && restCritical) onRestCriticalChange(false);
  }, [entitled, restCritical, onRestCriticalChange]);
  if (!entitled) return null;
  return <PressureNotice {...props} />;
}

function PressureNotice({
  segmentId,
  channel,
  restCritical,
  onRestCriticalChange,
  disabled,
}: SchedulePressureNoticeProps) {
  const pressure = useAsyncData(
    (signal) => fetchSegmentPressure(segmentId, channel, signal),
    [segmentId, channel],
  );
  const { data, status } = pressure;
  const offered = data !== null && data.bands.critical > 0;
  useEffect(() => {
    if (!offered && restCritical) onRestCriticalChange(false);
  }, [offered, restCritical, onRestCriticalChange]);

  if (status === 'error' && data === null) {
    return (
      <p className="text-[0.82rem] text-muted" data-role="schedule-pressure-unavailable">
        Message pressure could not be read for this send.
      </p>
    );
  }
  if (data === null) return null;

  const critical = data.bands.critical;
  const reaching = data.reachingCritical;
  if (critical === 0 && reaching === 0) return null;

  const parts: string[] = [];
  if (critical > 0) {
    parts.push(
      `${formatCount(critical)} ${critical === 1 ? 'person has' : 'people have'} had ${String(PRESSURE_CRITICAL_AT)} or more messages in the last ${String(PRESSURE_WINDOW_DAYS)} days`,
    );
  }
  if (reaching > 0) {
    parts.push(
      `this send would take ${formatCount(reaching)} more ${reaching === 1 ? 'person' : 'people'} to ${String(PRESSURE_CRITICAL_AT)} or more`,
    );
  }
  const sentence = `${parts.join('; ')}.`;
  const title =
    critical > 0 ? `${formatCount(critical)} at Critical` : `${formatCount(reaching)} would reach Critical`;

  return (
    <div className="flex flex-col gap-3" data-role="schedule-pressure">
      <Callout tone="warn" title={title}>
        {sentence.charAt(0).toUpperCase() + sentence.slice(1)}
      </Callout>
      {critical > 0 && (
        <Checkbox
          checked={restCritical}
          onCheckedChange={(value) => onRestCriticalChange(value === true)}
          disabled={disabled}
          label="Rest everyone at Critical for this send"
          description="Only this campaign — they show as Rested in its recipient list."
        />
      )}
    </div>
  );
}
